"use client"

import { forwardRef } from "react"
import { Icon } from "@/shared/components/icons"
import { motion, useReducedMotion } from "framer-motion"
import { cn } from "@/shared/lib/utils"

type TosViolationBannerProps = {
  message: string
  onDismiss: () => void
  className?: string
}

/**
 * Persistent alert above the prompt when the API blocks for Terms of Service / safety.
 */
export const TosViolationBanner = forwardRef<HTMLDivElement, TosViolationBannerProps>(
  function TosViolationBanner({ message, onDismiss, className }, ref) {
    const reduceMotion = useReducedMotion()

    return (
      <motion.div
        ref={ref}
        id="tos-violation-banner"
        role="alert"
        aria-live="assertive"
        initial={
          reduceMotion
            ? false
            : { opacity: 0, y: 10, filter: "blur(4px)" }
        }
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{
          type: "spring",
          stiffness: 420,
          damping: 32,
          mass: 0.85,
        }}
        className={cn(
          "relative mb-3 w-full overflow-hidden rounded-2xl",
          "border border-red-500/[0.22]",
          "bg-gradient-to-br from-red-950/75 via-[#1c0a0c]/92 to-zinc-950/88",
          "shadow-[0_0_0_1px_rgba(248,113,113,0.08),0_20px_48px_-16px_rgba(127,29,29,0.55),inset_0_1px_0_0_rgba(255,255,255,0.06)]",
          "backdrop-blur-xl backdrop-saturate-150",
          className
        )}
      >
        {/* Top sheen: reads as a polished system surface, not a flat error box */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-300/35 to-transparent"
          aria-hidden
        />

        <div className="relative flex items-start gap-3 px-3.5 py-3.5 sm:gap-4 sm:px-4 sm:py-4">
          {/* Left policy icon — distinct from dismiss so the affordance is unambiguous */}
          <div
            className={cn(
              "mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl",
              "bg-gradient-to-br from-red-600/90 to-rose-900/80",
              "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_4px_14px_-4px_rgba(185,28,28,0.65)]",
              "ring-1 ring-white/10"
            )}
            aria-hidden
          >
            <Icon name="shield-fail" size={18} className="size-[18px] text-red-50" />
          </div>

          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-red-300/85">
                Policy
              </p>
              <span className="hidden text-red-500/40 sm:inline" aria-hidden>
                ·
              </span>
              <p className="text-sm font-semibold leading-tight text-red-100">
                Terms of Service violation
              </p>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-zinc-200/95 [text-wrap:pretty]">
              {message}
            </p>
          </div>

          <button
            type="button"
            onClick={onDismiss}
            className={cn(
              "group/btn -mr-1 -mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl",
              "text-red-200/75 transition-colors",
              "hover:bg-white/[0.06] hover:text-zinc-100",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0a0c]"
            )}
            aria-label="Dismiss policy notice"
          >
            <Icon
              name="x"
              size={16}
              className="size-4 transition-transform duration-200 ease-out group-hover/btn:scale-105"
            />
          </button>
        </div>
      </motion.div>
    )
  }
)

TosViolationBanner.displayName = "TosViolationBanner"
